/**
 * Identity-pending banner.
 *
 * The API refuses every protected operation for an account that has not proved the identifier the policy requires (TASK-0062), so
 * the shell says so **before** the user discovers it as an error. The copy names the channel and links to the screen that finishes
 * the step; it deliberately no longer promises that only *some* features are affected, because the rule is that nothing is reachable
 * until the identifier is verified.
 *
 * The channel comes from the profile's `verification` object, which the server computes from the policy in force — the banner cannot
 * disagree with the API about what is missing.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/surface';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/features/auth/hooks/useAuth';

/** Warns the signed-in user that an identifier still has to be proved. */
export function IdentityPendingBanner() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const verification = user?.verification ?? null;

  if (!user || !verification || verification.isVerified || verification.state === 'Banned') {
    return null;
  }

  return (
    <Alert
      variant="warning"
      className="m-4 flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:mx-6 lg:mb-0"
    >
      <p className="flex-1 text-sm">
        {t(
          verification.channel === 'Mobile'
            ? 'auth.verification.bannerMobile'
            : 'auth.verification.bannerEmail',
        )}
      </p>

      <Button asChild size="sm" variant="outline">
        <Link to="/verify">{t('auth.verification.bannerCta')}</Link>
      </Button>
    </Alert>
  );
}
